/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  EditingState,
  SortingState,
  IntegratedSorting,
  DataTypeProvider,
  SearchState,
  IntegratedFiltering,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableHeaderRow,
  TableEditColumn,
  Toolbar,
  SearchPanel,
  DragDropProvider,
  Table,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  ColumnChooser,
  PagingPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { Command, PopupEditing } from '../../Shared';
import { getRowId, userroles } from '../../common';
import {
  getWorkTickets,
  getProducts,
  createWorkTicket,
  updateWorkTicket,
  deleteWorkTicket,
} from '../../graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  currencyFormatterEmpty,
  timeFormatter,
  userFormatter,
} from '../../Shared/colorFormat';
import { SearchTable } from '../../components';
import { getColumns } from '../../common/columns';
import { Typography } from '@material-ui/core';
import useEmployees from '../../hooks/useEmployees';
import { useTemplate, useUsers } from '../../hooks';
import { getTasks } from '../../graphql/query';
import { TableComponent } from '../components/TableComponent';
import PopupWorkTicket from '../../pubups/PopupWorkTicket';
import HRContext from '../../contexts/hr';

const WorkTickets = forwardRef((props: any, ref: any) => {
  const { isRTL, words, start, end, theme, company } = props;

  const col = getColumns({ isRTL, words });
  const { tempoptions } = useTemplate();

  const [columns] = useState(
    [
      { name: 'time', title: words?.time },
      { name: 'docNo', title: words?.no },
      { name: 'isWorking', title: isRTL ? 'حاضر' : 'Present' },
      { name: 'isAbsent', title: isRTL ? 'غائب' : 'Absent' },
      { name: 'overtime', title: isRTL ? 'اضافي' : 'Overtime' },
      { name: 'deduction', title: isRTL ? 'خصم' : 'Deduction' },
      { name: 'desc', title: words?.description },
      col.userId,
    ].filter((x) => x)
  );

  const [tableColumnExtensions]: any = useState([
    { columnName: 'time', width: 120 },
    { columnName: 'docNo', width: 120 },
    { columnName: 'isWorking', width: 120 },
    { columnName: 'isAbsent', width: 120 },
    { columnName: 'overtime', width: 120 },
    { columnName: 'deduction', width: 120 },
    { columnName: 'desc', width: 200 },
    { columnName: 'userId', width: 150 },
  ]);

  const [rows, setRows] = useState([]);
  const { activeemployees: employees } = useEmployees();
  const { users } = useUsers();

  const {
    state: { hiddenColumnNames },
    dispatch,
  } = useContext(HRContext);

  const setHiddenColumnNames = (hiddenColumns: any) => {
    dispatch({ type: 'setHiddenColumnNames', payload: hiddenColumns });
  };

  const [loadWorkTickets, opData]: any = useLazyQuery(getWorkTickets, {
    fetchPolicy: 'cache-and-network',
  });

  const refresQuery = {
    refetchQueries: [
      {
        query: getWorkTickets,
        variables: {
          start: start ? start.setHours(0, 0, 0, 0) : undefined,
          end: end ? end.setHours(23, 59, 59, 999) : undefined,
        },
      },
      { query: getProducts },
      { query: getTasks },
    ],
  };

  useEffect(() => {
    const variables = {
      start: start ? start.setHours(0, 0, 0, 0) : undefined,
      end: end ? end.setHours(23, 59, 59, 999) : undefined,
    };
    if (start && end) {
      loadWorkTickets({
        variables,
      });
    }
  }, [start, end]);

  const [addWorkTicket] = useMutation(createWorkTicket, refresQuery);
  const [editWorkTicket] = useMutation(updateWorkTicket, refresQuery);
  const [removeWorkTicket] = useMutation(deleteWorkTicket, refresQuery);

  useImperativeHandle(ref, () => ({
    refresh() {
      if (opData?.refetch) {
        opData.refetch();
      }
    },
  }));

  const commitChanges = async ({ deleted }) => {
    if (deleted) {
      const _id = deleted[0];
      removeWorkTicket({ variables: { _id } });
      setRows(rows.filter((row: any) => row._id !== _id));
    }
  };
  useEffect(() => {
    if (opData?.data?.getWorkTickets?.data) {
      const { data } = opData.data.getWorkTickets;
      setRows(data);
    }
  }, [opData]);

  return (
    <>
      <Grid rows={rows} columns={columns} getRowId={getRowId}>
        <SortingState />
        <EditingState onCommitChanges={commitChanges} />
        <SearchState />
        <PagingState defaultCurrentPage={0} defaultPageSize={10} />
        <IntegratedSorting />
        <IntegratedFiltering />
        <IntegratedPaging />
        <DragDropProvider />
        <Table
          messages={{
            noData: isRTL ? 'لا يوجد بيانات' : 'no data',
          }}
          tableComponent={TableComponent}
          rowComponent={(props: any) => (
            <Table.Row {...props} style={{ height: 60 }}></Table.Row>
          )}
          columnExtensions={tableColumnExtensions}
        />

        <TableColumnReordering
          defaultOrder={[
            'time',
            'docNo',
            'isWorking',
            'isAbsent',
            'overtime',
            'bonos',
            'deduction',
            'desc',
            'userId',
          ]}
        />
        <TableColumnResizing defaultColumnWidths={tableColumnExtensions} />

        <TableHeaderRow
          showSortingControls
          titleComponent={({ children }) => {
            return (
              <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                {children}
              </Typography>
            );
          }}
        />
        <TableColumnVisibility
          defaultHiddenColumnNames={hiddenColumnNames}
          hiddenColumnNames={hiddenColumnNames}
          onHiddenColumnNamesChange={setHiddenColumnNames}
        />
        <DataTypeProvider
          for={['time']}
          formatterComponent={timeFormatter}
        ></DataTypeProvider>
        <DataTypeProvider
          for={['userId']}
          formatterComponent={(props: any) =>
            userFormatter({ ...props, users })
          }
        ></DataTypeProvider>
        <DataTypeProvider
          for={['overtime', 'bonos', 'deduction']}
          formatterComponent={currencyFormatterEmpty}
        ></DataTypeProvider>
        <TableEditColumn
          showEditCommand={userroles?.sales?.edit}
          showDeleteCommand={!userroles?.isEmployee && userroles?.sales?.delete}
          showAddCommand={userroles?.sales?.add}
          commandComponent={Command}
        ></TableEditColumn>

        <Toolbar
          rootComponent={(props: any) => (
            <Toolbar.Root
              style={{
                position: 'absolute',
                left: isRTL ? 40 : undefined,
                right: isRTL ? undefined : 40,
                top: 62,
                zIndex: 115,
              }}
              {...props}
            ></Toolbar.Root>
          )}
        />
        <ColumnChooser />
        <PagingPanel
          pageSizes={
            tempoptions?.pageall ? [5, 10, 20, 50, 0] : [5, 10, 20, 50]
          }
        />

        <SearchPanel
          inputComponent={(props: any) => {
            return <SearchTable isRTL={isRTL} {...props}></SearchTable>;
          }}
        />
        <PopupEditing addAction={addWorkTicket} editAction={editWorkTicket}>
          <PopupWorkTicket
            employees={employees}
            company={company}
            theme={theme}
          ></PopupWorkTicket>
        </PopupEditing>
      </Grid>
    </>
  );
});

export default WorkTickets;
